import React, { ReactElement, ReactNode, useState } from 'react';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';

import { Button } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { StyleVariant, Text } from '@/components/TextComponent';
import { Box, Column } from '@/components/lib/components';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

export type InfoSheetProps = {
    title?: string;
    titleVariant?: StyleVariant;
    description: string | ReactNode;
    buttonLabel?: string | ReactElement;
    handlePress?: () => void;
    handleSecondaryPress?: () => void;
    deleteButtonLabel?: string;
    deleteButtonActionPress?: () => void;
    showCancel?: boolean;
    errorStyling?: boolean;
    secondaryLabel?: string;
    icon?: ReactNode;
    onPressCancel?: () => void;
    footerContent?: ReactNode;
    dismissible?: boolean;
    backgroundColor?: string;
    primaryButtonTestId?: string;
    containerStyle?: StyleProp<ViewStyle>;
    secondaryButtonTestId?: string;
};

export const InfoSheet = (props: InfoSheetProps & { modalRef: React.RefObject<BottomSheetModalMethods | null> }) => {
    const {
        modalRef,
        title,
        titleVariant,
        showCancel,
        description,
        errorStyling,
        buttonLabel,
        secondaryLabel,
        handlePress,
        handleSecondaryPress,
        deleteButtonLabel,
        deleteButtonActionPress,
        icon,
        onPressCancel,
        footerContent,
        primaryButtonTestId,
        secondaryButtonTestId,
        containerStyle,
    } = props;

    const [isLoading, setIsLoading] = useState(false);

    const handleOnPress = async () => {
        if (!isLoading && handlePress) {
            setIsLoading(true);
            await handlePress();
            setIsLoading(false);
        }
        modalRef.current?.dismiss();
    };

    const handleDeletePress = () => {
        if (!isLoading && deleteButtonActionPress) {
            setIsLoading(true);
            deleteButtonActionPress();
            setIsLoading(false);
        }
        modalRef.current?.dismiss();
    };

    return (
        <Column
            width={'100%'}
            paddingHorizontal={'s16'}
            pt={'s16'}
            paddingBottom={'s16'}
            style={[styles.column, containerStyle]}
        >
            {icon ? (
                <Box alignItems="center">
                    {icon}
                    <SizedBox value={20} />
                </Box>
            ) : null}
            {title ? (
                <Text variant={titleVariant ?? 'headlineMedium'} textAlign={'center'} testID="modalTitle">
                    {title}
                </Text>
            ) : null}
            <SizedBox value={8} />
            {typeof description === 'string' ? (
                <Text
                    color={'neutral300'}
                    textAlign={'center'}
                    variant="bodyMedium"
                    testID="modalDescription"
                    px={'s24'}
                >
                    {description}
                </Text>
            ) : (
                description
            )}
            {footerContent ? footerContent : <SizedBox value={32} />}
            {deleteButtonLabel ? (
                <>
                    <Button
                        label={deleteButtonLabel}
                        variant="danger"
                        onPress={handleDeletePress}
                        loading={isLoading}
                        testID="delete-button"
                    />
                    <SizedBox value={12} />
                </>
            ) : null}
            <Box gap={'s12'}>
                {buttonLabel ? (
                    <Box py={secondaryLabel || showCancel ? undefined : 's16'}>
                        <Button
                            label={buttonLabel}
                            variant={errorStyling ? 'alert' : undefined}
                            hierarchy={'primary'}
                            onPress={handleOnPress}
                            loading={isLoading}
                            testID={primaryButtonTestId}
                        />
                    </Box>
                ) : null}
                {secondaryLabel ? (
                    <>
                        <Button
                            label={secondaryLabel}
                            variant={'light'}
                            onPress={() => {
                                if (handleSecondaryPress) {
                                    handleSecondaryPress();
                                }
                                modalRef.current?.dismiss();
                            }}
                            disabled={isLoading}
                            testID={secondaryButtonTestId}
                        />
                    </>
                ) : null}
                {showCancel ? (
                    <>
                        <Button
                            label={'Cancel'}
                            variant={'light'}
                            onPress={() => {
                                if (onPressCancel) {
                                    onPressCancel();
                                }
                                modalRef.current?.dismiss();
                            }}
                        />
                        <SizedBox value={16} />
                    </>
                ) : null}
            </Box>
        </Column>
    );
};

const styles = StyleSheet.create({
    column: {
        paddingBottom: 16,
    },
});
